import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, Users, User, Check, X, Quote, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import hyroxLogo from "@assets/hyrox_logo_opt.png";
import pooleTrainingFloor from "@/assets/poole/poole-training-floor-card.jpg";
import bournemouthAirportCard from "@/assets/choose-home-club/bournemouth-airport-card-bright.jpg";
import { SITES } from "@/lib/siteConfig";

import gymWideView from "@assets/DSC04533_1760639937116_opt.jpg";
import gymWideViewWebp from "@assets/DSC04533_1760639937116_opt.webp";
import trainingSession from "@assets/DSC05970_1760640029834_opt.jpg";
import trainingSessionWebp from "@assets/DSC05970_1760640029834_opt.webp";
import trainingWomanKettlebell from "@assets/DSC04454_1760640029829_opt.jpg";
import trainingWomanKettlebellWebp from "@assets/DSC04454_1760640029829_opt.webp";

const faqs = [
  {
    q: "What does Exclusive Use mean?",
    a: "The whole gym is yours for a private session. Train alone or bring your own group.",
    link: "/first-session",
    linkText: "Explore your first session"
  },
  {
    q: "How many people can use the gym at once?",
    a: `Shared sessions are limited to ${SITES.bournemouthAirport.maxCapacity} people at Bournemouth Airport and ${SITES.poole.maxCapacity} at Poole.`
  },
  {
    q: "Do I need to book every session?",
    a: "Yes. Members and visitors book their training time in advance.",
    link: "/how-it-works",
    linkText: "See how it works"
  },
  {
    q: "Can I train with friends or family?",
    a: "Yes. Book the whole gym and bring your own group."
  },
  {
    q: "Can I bring my own personal trainer?",
    a: "Yes. Personal trainers are welcome to train clients here after sharing their insurance details with us."
  },
  {
    q: "Is Effortless suitable for beginners?",
    a: "Absolutely. Enjoy more space, privacy and a calmer place to train."
  },
  {
    q: "Are children allowed?",
    a: "Account holders must be 18 or over. Children can attend with a parent during an Exclusive Use session. Under sixes cannot use equipment."
  },
  {
    q: "Where are the gyms?",
    a: `Find us at Bournemouth Airport, ${SITES.bournemouthAirport.addressLine}, and in Poole, ${SITES.poole.addressLine}.`
  },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  useEffect(() => {
    document.title = "Effortless Gym - Private Gyms in Bournemouth & Poole | Exclusive Use, No Crowds";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Private gyms in Bournemouth and Poole. Book the whole gym to yourself or train in deliberately low-capacity shared sessions. More space, privacy and no waiting for equipment.');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://effortlessgym.co.uk/');
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="page-home">
      <Navigation />
      <Hero />

      {/* Two ways to train */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold mb-3 tracking-tight">Two Ways To Train</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose complete privacy or one of our deliberately low-capacity shared sessions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 lg:gap-7">
            <div className="min-w-0 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/40 rounded-2xl p-6 sm:p-8 relative" data-testid="card-exclusive-use">
              <span className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-semibold tracking-wide px-3 py-1 rounded-full">
                EXCLUSIVE USE
              </span>
              <User className="h-10 w-10 text-primary mb-5" />
              <h3 className="text-2xl font-alata font-bold mb-4">The Whole Gym Is Yours</h3>
              <ul className="space-y-3 mb-7">
                {[
                  "Train completely privately",
                  "Come alone or bring your own group",
                  "Bring your own PT",
                  "No waiting for equipment",
                  "No strangers sharing your session",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link href="/first-session">
                <Button className="w-full sm:w-auto h-auto min-h-10 py-3 whitespace-normal" data-testid="button-exclusive-use-home">
                  Try It For £14.99 <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                </Button>
              </Link>
            </div>

            <div className="min-w-0 bg-card border border-border rounded-2xl p-6 sm:p-8 flex flex-col" data-testid="card-shared-session">
              <Users className="h-10 w-10 text-primary mb-5" />
              <span className="text-primary text-xs font-semibold tracking-widest uppercase mb-2">SHARED SESSION</span>
              <h3 className="text-2xl font-alata font-bold mb-4">Low Capacity. More Space.</h3>
              <ul className="space-y-3 mb-7">
                {[
                  `Maximum ${SITES.bournemouthAirport.maxCapacity} in Bournemouth`,
                  `Maximum ${SITES.poole.maxCapacity} in Poole`,
                  "Space to train properly",
                  "No fighting for equipment",
                  "Premium kit without the crowds",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link href="/membership" className="mt-auto">
                <Button variant="outline" className="w-full sm:w-auto border-primary text-primary hover:bg-primary/10" data-testid="button-memberships-home">
                  View Memberships <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Suitability */}
      <section className="py-14 sm:py-20 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold tracking-tight">Is Effortless Gym For You?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 lg:gap-7">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="card-who-its-for">
              <h3 className="text-lg font-alata font-bold mb-5 text-primary">THIS GYM IS FOR YOU IF...</h3>
              <ul className="space-y-4">
                {[
                  "You want privacy and space to train",
                  "You value your time and don't want to wait for equipment",
                  "Busy gyms make you uncomfortable or put you off training",
                  "You want a clean, calm, premium place to train",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="card-who-its-not-for">
              <h3 className="text-lg font-alata font-bold mb-5 text-muted-foreground">IT PROBABLY ISN'T FOR YOU IF...</h3>
              <ul className="space-y-4">
                {[
                  "You enjoy busy, high-energy commercial gyms",
                  "You want to turn up without booking",
                  "Your only priority is finding the cheapest gym membership",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" /><span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-8 sm:mt-10">
            <p className="text-xl font-alata font-bold mb-2">Different by design.</p>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
              Effortless Gym deliberately limits the number of people who can train at once so you get more space, more privacy and more time actually training.
            </p>
            <Link href="/first-session">
              <Button size="lg" className="max-w-full h-auto min-h-11 whitespace-normal px-5 sm:px-8 py-3" data-testid="button-first-session-middle">Book Your £14.99 First Session <ArrowRight className="w-5 h-5 ml-2 shrink-0" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-14 sm:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold mb-3 tracking-tight">Two Private Gyms. One Standard.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Private training, Exclusive Use and deliberately low-capacity shared sessions at both locations.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Link href={SITES.bournemouthAirport.path}>
              <div className="h-full bg-background border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-colors cursor-pointer" data-testid="card-home-bournemouth-airport">
                <img src={bournemouthAirportCard} alt="Effortless Gym Bournemouth Airport interior" className="w-full h-40 sm:h-48 object-cover dark-blend-img" loading="lazy" width={1000} height={625} data-testid="img-home-bournemouth-airport" />
                <div className="p-6 sm:p-8">
                  <span className="inline-block bg-primary/10 text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4">Open Now</span>
                  <h3 className="text-xl sm:text-2xl font-alata font-bold mb-2">Bournemouth Airport</h3>
                  <p className="text-muted-foreground mb-4">Private low-capacity gym. Shared sessions: maximum {SITES.bournemouthAirport.maxCapacity} people. Exclusive Use, 24/7 member access, free parking, a custom Wolverson rig, full free-weights and cardio collection.</p>
                  <p className="text-sm text-muted-foreground mb-4">{SITES.bournemouthAirport.addressLine}</p>
                  <span className="text-primary font-medium inline-flex items-center gap-1">Explore Bournemouth <ArrowRight className="w-4 h-4" /></span>
                </div>
              </div>
            </Link>
            <Link href={SITES.poole.path}>
              <div className="h-full bg-background border border-primary/30 rounded-2xl overflow-hidden hover:border-primary/60 transition-colors cursor-pointer" data-testid="card-home-poole">
                <img src={pooleTrainingFloor} alt="Training floor at Effortless Gym Poole" className="w-full h-40 sm:h-48 object-cover dark-blend-img" loading="lazy" width={960} height={540} data-testid="img-home-poole-exterior" />
                <div className="p-6 sm:p-8">
                  <span className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
                    Open Now <img src={hyroxLogo} alt="HYROX" className="h-3 w-auto brightness-0 invert" width={70} height={10} loading="lazy" />
                  </span>
                  <h3 className="text-xl sm:text-2xl font-alata font-bold mb-2">Poole</h3>
                  <p className="text-muted-foreground mb-4">Our larger private training space, HYROX-affiliated, with Exclusive Use and shared sessions capped at {SITES.poole.maxCapacity} people. 24/7 member access and free parking.</p>
                  <p className="text-sm text-muted-foreground mb-4">{SITES.poole.addressLine}</p>
                  <span className="text-primary font-medium inline-flex items-center gap-1">Explore Poole <ArrowRight className="w-4 h-4" /></span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-14 sm:py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16">
            <p className="text-lg sm:text-xl text-muted-foreground mb-3">People don't join Effortless because they want another gym.</p>
            <p className="text-2xl sm:text-3xl font-alata font-bold leading-snug">They join because they want a better place to train.</p>
          </div>
          <h2 className="text-3xl sm:text-4xl font-alata font-bold tracking-tight text-center mb-8 sm:mb-10">What Members Say</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-robert">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"It's a nice, clean and private environment enabling me to train without waiting for equipment."</p>
              <p className="font-semibold text-primary text-sm">Robert</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-bryony">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"Room for 4 people to train at a time so no overcrowding or waiting for machines."</p>
              <p className="font-semibold text-primary text-sm">Bryony</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-olivia">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"Only a few people can train at a time, which makes every workout feel personal and focused."</p>
              <p className="font-semibold text-primary text-sm">Olivia</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-cheyenne">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"Training here makes you feel so much more comfortable and confident."</p>
              <p className="font-semibold text-primary text-sm">Cheyenne</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-katrina">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"The space is beautifully set up, welcoming, safe and has such a positive atmosphere."</p>
              <p className="font-semibold text-primary text-sm">Katrina</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 relative" data-testid="testimonial-lynne">
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/20" />
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              <p className="text-foreground mb-4">"A great place for people to work out with family or on their own."</p>
              <p className="font-semibold text-primary text-sm">Lynne</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a href="https://www.google.com/maps/place/Effortless+Gym/@50.7850763,-1.8551564,17z/data=!4m8!3m7!1s0x487399770c6f406f:0x8d1c525c58cf2cfe!8m2!3d50.7850763!4d-1.8551564!9m1!1b1!16s%2Fg%2F11yj57_dc7?entry=ttu" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors" data-testid="link-google-reviews">
              See all reviews on Google <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 sm:py-16 bg-card border-y border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold tracking-tight">How It Works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { number: "01", title: "Choose Your Gym", text: "Bournemouth Airport or Poole." },
              { number: "02", title: "Book Your Training Time", text: "Book your training time so you know there'll be space when you arrive." },
              { number: "03", title: "Turn Up And Train", text: "Enjoy more space, more privacy and no fighting for equipment." },
            ].map((step) => (
              <div key={step.number} className="bg-background border border-border rounded-xl p-6">
                <span className="text-primary font-alata text-2xl font-bold">{step.number}</span>
                <h3 className="font-alata font-bold text-xl mt-3 mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-7">
            <Link href="/how-it-works">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">See How It Works <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              First time here? <Link href="/first-session" className="text-primary font-medium hover:underline">Book your £14.99 first session <span aria-hidden="true">→</span></Link>
            </p>
          </div>
        </div>
      </section>

      {/* Personal Trainers Welcome */}
      <section className="py-14 sm:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-alata font-bold mb-5 tracking-tight">Personal Trainers Welcome</h2>
              <p className="text-lg text-muted-foreground mb-6">Train your clients here without battling for space or equipment.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span>Book sessions like anyone else</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span>Calm environment for client confidence</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span>Premium equipment, no waiting</span></li>
                <li className="flex items-start gap-3"><Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span>Just share your insurance details with us before using the gym</span></li>
              </ul>
              <a href="https://effortlessgym.co.uk/personal-trainer-gym-space-bournemouth" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="button-pt-info">
                  Learn more for PTs <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
            <div className="img-overlay">
              <picture>
                <source srcSet={trainingSessionWebp} type="image/webp" />
                <img src={trainingSession} alt="Personal training session at Effortless Gym" className="w-full h-80 object-cover rounded-2xl" loading="lazy" width={800} height={320} data-testid="img-pt-section" />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 sm:py-20 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold mb-3 tracking-tight">See The Space</h2>
            <p className="text-lg text-muted-foreground">Premium equipment. Calm environment. Room to breathe.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="img-overlay">
              <picture>
                <source srcSet={gymWideViewWebp} type="image/webp" />
                <img src={gymWideView} alt="Effortless Gym premium facility - wide view" className="w-full h-72 sm:h-80 md:h-96 object-cover rounded-2xl" loading="lazy" width={800} height={384} data-testid="img-gym-wide" />
              </picture>
            </div>
            <div className="img-overlay">
              <picture>
                <source srcSet={trainingWomanKettlebellWebp} type="image/webp" />
                <img src={trainingWomanKettlebell} alt="Training at Effortless Gym" className="w-full h-72 sm:h-80 md:h-96 object-cover rounded-2xl" loading="lazy" width={800} height={384} data-testid="img-training" />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-9">
            <h2 className="text-3xl sm:text-4xl font-alata font-bold tracking-tight">Common Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`faq-item-${index}`}>
                <button
                  type="button"
                  className="w-full px-5 sm:px-6 py-5 text-left flex items-center justify-between gap-4"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                  data-testid={`button-faq-toggle-${index}`}
                >
                  <span className="font-semibold text-foreground">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === index ? "rotate-180" : ""}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 sm:px-6 pb-5">
                    <p className="text-muted-foreground">
                      {faq.a}
                      {"link" in faq && faq.link && (
                        <>
                          {" "}
                          <Link href={faq.link} className="text-primary font-medium hover:underline">{faq.linkText}</Link>
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-20 bg-gradient-to-br from-primary/15 via-primary/10 to-background border-y border-primary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-alata font-bold mb-5 tracking-tight">Experience Effortless</h2>
          <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            The best way to understand the difference is to train here. Your first session gives you the whole gym to yourself for 60 minutes for just £14.99. Come alone or bring your group.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/first-session" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto h-auto min-h-12 whitespace-normal px-5 sm:px-8 py-3 sm:py-6 text-lg font-medium" data-testid="button-first-session-final">Book Your £14.99 First Session <ArrowRight className="w-5 h-5 ml-2 shrink-0" /></Button>
            </Link>
            <Link href="/membership" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full border-primary text-primary hover:bg-primary/10">View Memberships</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}